/**
 * "Make slides" from a note, a paper, a manuscript or the project: choose about how many slides and,
 * if useful, who will listen; BrainLog's AI writes a Slides note in the "Slides" folder.
 */
import { useState } from "react";
import { Presentation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RunFlow } from "@/features/ai/RunFlow";

export type SlidesScope = "note" | "paper" | "manuscript" | "project";

const COUNTS = [5, 8, 10, 15, 20];

export function MakeSlidesFlow({ workspaceId, scope, id, onBack }: { workspaceId: string; scope: SlidesScope; id?: string; onBack?(): void }) {
  const [count, setCount] = useState(10);
  const [audience, setAudience] = useState("");
  const [params, setParams] = useState<Record<string, unknown> | null>(null);
  if (params) return <RunFlow action="make_slides" workspaceId={workspaceId} params={params} onBack={() => (onBack ? onBack() : setParams(null))} />;
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        setParams({ scope, ...(id ? { id } : {}), count, ...(audience.trim() ? { audience: audience.trim() } : {}) });
      }}
    >
      <div className="space-y-1">
        <p id="slides-count" className="text-sm font-medium">
          About how many slides
        </p>
        <div className="flex flex-wrap gap-1" role="radiogroup" aria-labelledby="slides-count">
          {COUNTS.map((n) => (
            <Button key={n} type="button" size="sm" variant={count === n ? "default" : "outline"} role="radio" aria-checked={count === n} onClick={() => setCount(n)}>
              {n}
            </Button>
          ))}
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="slides-audience">For whom, and how long (optional)</Label>
        <Input id="slides-audience" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="e.g. lab meeting, 10 minutes" maxLength={300} />
      </div>
      <Button type="submit" size="sm">
        <Presentation /> Continue
      </Button>
    </form>
  );
}

const SCOPE_TEXT: Record<SlidesScope, string> = {
  note: "this note",
  paper: "this paper",
  manuscript: "this report or manuscript",
  project: "this project (its overview, proposal and recent records)",
};

export function MakeSlidesDialog({ workspaceId, scope, id, onClose }: { workspaceId: string; scope: SlidesScope; id?: string; onClose(): void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Make slides</DialogTitle>
          <DialogDescription>
            Slides from {SCOPE_TEXT[scope]}: titles, short points and speaker notes, saved as a Slides note in the “Slides” folder. Edit it like any note, then choose Present.
          </DialogDescription>
        </DialogHeader>
        <MakeSlidesFlow workspaceId={workspaceId} scope={scope} id={id} onBack={onClose} />
      </DialogContent>
    </Dialog>
  );
}

export function MakeSlidesButton({
  workspaceId,
  scope,
  id,
  size = "sm",
  variant = "outline",
}: {
  workspaceId: string;
  scope: SlidesScope;
  id?: string;
  size?: "sm" | "xs";
  variant?: "default" | "outline" | "ghost";
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size={size} variant={variant} onClick={() => setOpen(true)} title="Slides for a talk, made by BrainLog's AI and saved as a note">
        <Presentation /> Make slides
      </Button>
      {open && <MakeSlidesDialog workspaceId={workspaceId} scope={scope} id={id} onClose={() => setOpen(false)} />}
    </>
  );
}
